// SqlDialog.js - SQL generation and export dialog
import React from 'react';
import { X, Copy, Download, Database, Play } from 'lucide-react';

const SqlDialog = ({
  isOpen,
  onClose,
  sqlCommand,
  onCopy,
  copied,
  onExecute,
  executing,
  executionResult,
  className = ''
}) => {
  if (!isOpen) return null;

  const downloadSqlFile = () => {
    const blob = new Blob([sqlCommand], { type: 'text/sql' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'itinerary_sql_commands.sql';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="w-full h-full flex items-center justify-center">
        <div className={`max-w-4xl w-full max-h-[90vh] bg-white rounded-lg shadow-xl flex flex-col ${className}`}>
          {/* Header */}
          <div className="flex justify-between items-center p-6 border-b border-gray-200">
            <div className="flex items-center gap-3">
              <Database className="w-6 h-6 text-purple-600" />
              <h2 className="text-xl font-semibold text-gray-800">
                Generated SQL Commands
              </h2>
            </div>
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700 transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-hidden p-6">
            <div className="mb-4">
              <p className="text-sm text-gray-600 mb-4">
                Below are the SQL commands to insert your itinerary data into the database.
                You can copy these commands or download them as a .sql file.
              </p>

              {/* Action Buttons */}
              <div className="flex gap-3 mb-4">
                <button
                  onClick={onExecute}
                  disabled={executing || !sqlCommand}
                  className="px-4 py-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600 flex items-center gap-2 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
                >
                  <Play className="w-4 h-4" />
                  {executing ? 'Executing...' : 'Run SQL on Database'}
                </button>

                <button
                  onClick={onCopy}
                  className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 flex items-center gap-2 transition-colors"
                >
                  <Copy className="w-4 h-4" />
                  {copied ? 'Copied!' : 'Copy to Clipboard'}
                </button>

                <button
                  onClick={downloadSqlFile}
                  className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 flex items-center gap-2 transition-colors"
                >
                  <Download className="w-4 h-4" />
                  Download SQL File
                </button>
              </div>

              {/* Execution Result */}
              {executionResult && (
                <div className={`mb-4 p-4 rounded-lg border ${
                  executionResult.success
                    ? 'bg-green-50 border-green-200'
                    : 'bg-red-50 border-red-200'
                }`}>
                  <p className={`text-sm font-medium ${
                    executionResult.success ? 'text-green-800' : 'text-red-800'
                  }`}>
                    {executionResult.success ? '✓ Success' : '✗ Error'}
                  </p>
                  <p className={`text-sm mt-1 ${
                    executionResult.success ? 'text-green-700' : 'text-red-700'
                  }`}>
                    {executionResult.message}
                  </p>
                  {executionResult.affectedRows !== undefined && (
                    <p className="text-sm text-green-600 mt-1">
                      Affected rows: {executionResult.affectedRows}
                    </p>
                  )}
                </div>
              )}
            </div>

            {/* SQL Content */}
            <div className="bg-gray-50 rounded-lg border border-gray-200 flex-1 overflow-hidden flex flex-col">
              <div className="p-3 border-b border-gray-200 bg-gray-100 rounded-t-lg">
                <span className="text-sm font-medium text-gray-700">SQL Commands</span>
              </div>
              <div className="flex-1 overflow-auto p-4">
                <pre className="text-sm text-gray-800 whitespace-pre-wrap font-mono bg-white p-4 rounded border border-gray-200 min-h-full">
                  {sqlCommand || 'No SQL commands generated yet.'}
                </pre>
              </div>
            </div>

            {/* Instructions */}
            <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <h3 className="font-medium text-blue-800 mb-2">Instructions:</h3>
              <ul className="text-sm text-blue-700 space-y-1">
                <li>• Click "Run SQL on Database" to execute directly on your database</li>
                <li>• Or copy the SQL commands and run them manually</li>
                <li>• Or download as a .sql file for later use</li>
                <li>• The commands use INSERT ... ON DUPLICATE KEY UPDATE for safe updates</li>
              </ul>
            </div>
          </div>

          {/* Footer */}
          <div className="flex justify-end gap-3 p-6 border-t border-gray-200 bg-gray-50">
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SqlDialog;